"use client";

/**
 * Marketing-only CTA wrappers: presentational SoT is
 * `@repo/ui/components/design-system/cta/*`. Use these when you need
 * `landing_cta_clicked` / identity lifecycle signals. Operate surfaces import DS CTAs directly.
 */
import { useMarketingAnalytics } from "@analytics";
import { useRecordIdentityLifecycleSignalOnAction } from "@analytics/lib/use-record-identity-lifecycle-signal-on-action";
import {
	PrimaryCta,
	type PrimaryCtaProps as PrimaryCtaBaseProps,
} from "@repo/ui/components/design-system/cta/primary-cta";
import {
	SecondaryCta,
	type SecondaryCtaProps as SecondaryCtaBaseProps,
} from "@repo/ui/components/design-system/cta/secondary-cta";
import type { SafeRippleButtonProps } from "@repo/ui/components/material-ui/ripple-button";
import { forwardRef, useCallback } from "react";

export interface MarketingPrimaryCtaProps extends PrimaryCtaBaseProps {
	analyticsLocation?: string;
	analyticsCta?: string;
	identityLifecycleSignal?: string;
	identityLifecycleStorageKey?: string;
}

export interface MarketingSecondaryCtaProps extends SecondaryCtaBaseProps {
	analyticsLocation?: string;
	analyticsCta?: string;
}

function useMarketingCtaClick(params: {
	analyticsLocation?: string;
	analyticsCta?: string;
	identityLifecycleSignal?: string;
	identityLifecycleStorageKey?: string;
	onClick?: SafeRippleButtonProps["onClick"];
	withIdentity?: boolean;
}) {
	const { landingCtaClicked } = useMarketingAnalytics();
	const recordIdentityLifecycleSignal =
		useRecordIdentityLifecycleSignalOnAction(
			params.identityLifecycleSignal ?? "",
			params.identityLifecycleStorageKey ?? "",
		);

	return useCallback<NonNullable<SafeRippleButtonProps["onClick"]>>(
		(event) => {
			if (
				params.withIdentity &&
				params.identityLifecycleSignal &&
				params.identityLifecycleStorageKey
			) {
				recordIdentityLifecycleSignal();
			}
			if (params.analyticsLocation != null && params.analyticsCta != null) {
				const target = event.currentTarget as HTMLElement;
				let href: string | undefined;
				if (target instanceof HTMLAnchorElement) {
					href = target.href;
				} else {
					const anchor = target.closest("a");
					if (anchor instanceof HTMLAnchorElement) {
						href = anchor.href;
					}
				}
				landingCtaClicked({
					cta: params.analyticsCta,
					location: params.analyticsLocation,
					...(href != null ? { href } : {}),
				});
			}
			params.onClick?.(event);
		},
		[
			landingCtaClicked,
			params.analyticsCta,
			params.analyticsLocation,
			params.identityLifecycleSignal,
			params.identityLifecycleStorageKey,
			params.onClick,
			params.withIdentity,
			recordIdentityLifecycleSignal,
		],
	);
}

export const MarketingPrimaryCta = forwardRef<
	HTMLButtonElement,
	MarketingPrimaryCtaProps
>(function MarketingPrimaryCta(
	{
		analyticsLocation,
		analyticsCta,
		identityLifecycleSignal,
		identityLifecycleStorageKey,
		onClick,
		...props
	},
	ref,
) {
	const handleClick = useMarketingCtaClick({
		analyticsLocation,
		analyticsCta,
		identityLifecycleSignal,
		identityLifecycleStorageKey,
		onClick,
		withIdentity: true,
	});
	return <PrimaryCta ref={ref} {...props} onClick={handleClick} />;
});

export function MarketingSecondaryCta({
	analyticsLocation,
	analyticsCta,
	onClick,
	...props
}: MarketingSecondaryCtaProps) {
	const handleClick = useMarketingCtaClick({
		analyticsLocation,
		analyticsCta,
		onClick,
		withIdentity: false,
	});
	return <SecondaryCta {...props} onClick={handleClick} />;
}
